/**
 * Moments — planos & contratações, na mesma gramática de moments.tsx
 * (material temático, física de mola, comentários em português).
 *
 * Cerimônias: mudança para o Free (a vela segue acesa) · Starter (a pena
 * ganha brasa) · Professional (o eclipse — a coroa vaza) · Unlimited (a
 * coroa de constelação gira) · contratação de profissional (o contrato de
 * dois lacres).
 */
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { Scene, SceneText, useAutoDone, BornCandle } from './moments';

/* ───────────────────────── plano Free: a chama continua sua ───────────────────────── */
function PlanFreeMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 3800);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-6 px-6">
        <motion.div
          initial={{ y: 20, opacity: 0, scale: 2.2 }}
          animate={{ y: 0, opacity: 1, scale: 2.2 }}
          transition={{ type: 'spring', stiffness: 120, damping: 16 }}
          style={{ transformOrigin: 'center bottom' }}
          className="relative"
        >
          <BornCandle delay={0.5} />
          {/* halo modesto — sem exagero, é o plano gratuito */}
          <motion.span
            aria-hidden
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.5, 0.35] }}
            transition={{ delay: 0.6, duration: 1.4, times: [0, 0.4, 1] }}
            className="pointer-events-none absolute -inset-5 rounded-full"
            style={{ background: 'radial-gradient(circle at 50% 22%, rgba(246,233,200,0.22), transparent 62%)' }}
          />
        </motion.div>
        <SceneText
          delay={0.85}
          eyebrow={t('hud.planFreeEyebrow', 'a jornada a pé')}
          title={t('hud.planFreeTitle', 'A chama continua sua.')}
        />
      </div>
    </Scene>
  );
}

/* ───────────────────────── plano Starter: a pena ganha brasa ───────────────────────── */
const PLAN_STARTER_SPARKS = [
  { x: -34, y: -46, d: 0.0 },
  { x: 30, y: -58, d: 0.14 },
  { x: 4, y: -72, d: 0.26 },
];

function PlanStarterMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4200);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <motion.div
          initial={{ scale: 0.6, opacity: 0, rotate: -10 }}
          animate={{ scale: 1, opacity: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 200, damping: 13 }}
          className="relative"
        >
          {/* a pena, ainda fria */}
          <motion.span
            aria-hidden
            initial={{ filter: 'drop-shadow(0 0 0px rgba(246,233,200,0))' }}
            animate={{ filter: 'drop-shadow(0 0 20px rgba(246,233,200,0.95))' }}
            transition={{ delay: 1.0, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="block text-[44px] leading-none"
          >
            🪶
          </motion.span>
          {/* a brasa varre e acende a pena */}
          <motion.span
            aria-hidden
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.9, 0.6] }}
            transition={{ delay: 0.9, duration: 1.2, times: [0, 0.4, 1] }}
            className="pointer-events-none absolute -inset-6 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(246,233,200,0.45), transparent 62%)' }}
          />
          {PLAN_STARTER_SPARKS.map((sp, i) => (
            <motion.span
              key={i}
              aria-hidden
              initial={{ x: 0, y: 0, opacity: 0, scale: 0.4 }}
              animate={{ x: sp.x, y: sp.y, opacity: [0, 1, 0], scale: [0.4, 1, 0.6] }}
              transition={{ delay: 1.3 + sp.d, duration: 1.1, ease: 'easeOut' }}
              className="pointer-events-none absolute left-1/2 top-1/2 text-[11px]"
              style={{ color: 'var(--ember-hot)' }}
            >
              ✦
            </motion.span>
          ))}
        </motion.div>
        <SceneText
          delay={2.0}
          warm
          eyebrow={t('hud.planStarterEyebrow', 'plano starter')}
          title={t('hud.planStarterTitle', 'Sua pena ganhou brasa.')}
        />
      </div>
    </Scene>
  );
}

/* ───────────────────────── plano Professional: o eclipse ───────────────────────── */
function PlanProfessionalMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4800);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-8 px-6">
        <div className="relative h-[130px] w-[130px]">
          {/* o sol pleno, antes do eclipse */}
          <motion.div
            aria-hidden
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 rounded-full"
            style={{
              background: 'radial-gradient(circle at 42% 38%, #FFE1AE, #F6E9C8 60%, #C99A3E 96%)',
              boxShadow: '0 0 70px rgba(246,233,200,0.6), 0 0 140px rgba(246,233,200,0.28)',
            }}
          />
          {/* o disco escuro desliza por cima — só a coroa vaza pela borda */}
          <motion.div
            aria-hidden
            initial={{ x: -170, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 1.8, ease: [0.65, 0, 0.35, 1] }}
            className="absolute inset-0 rounded-full"
            style={{ background: '#0A101F', boxShadow: '0 0 60px rgba(246,233,200,0.8)' }}
          />
        </div>
        <SceneText
          delay={2.6}
          warm
          eyebrow={t('hud.planProEyebrow', 'plano professional')}
          title={t('hud.planProTitle', 'O eclipse: a noite inteira é sua.')}
        />
      </div>
    </Scene>
  );
}

/* ───────────────────────── plano Unlimited: a coroa de constelação ───────────────────────── */
const STAR_RING12 = Array.from({ length: 12 }, (_, i) => {
  const a = (i / 12) * Math.PI * 2 - Math.PI / 2;
  return { x: Math.cos(a) * 64, y: Math.sin(a) * 64, d: i * 0.08 };
});

function PlanUnlimitedMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 5000);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-8 px-6">
        <motion.div
          className="relative h-[160px] w-[160px]"
          initial={{ rotate: 0 }}
          animate={{ rotate: 30 }}
          transition={{ delay: 2.2, duration: 2.5, ease: [0.22, 1, 0.36, 1] }}
        >
          {STAR_RING12.map((st, i) => (
            <motion.span
              key={i}
              aria-hidden
              initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
              animate={{ x: st.x, y: st.y, opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 + st.d, type: 'spring', stiffness: 240, damping: 14 }}
              className="absolute left-1/2 top-1/2 -ml-[5px] -mt-[5px] text-[12px]"
              style={{ color: 'var(--ember-hot)', filter: 'drop-shadow(0 0 8px rgba(246,233,200,0.85))' }}
            >
              ✦
            </motion.span>
          ))}
          <motion.span
            aria-hidden
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 1.5, type: 'spring', stiffness: 220, damping: 13 }}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[34px]"
            style={{ color: 'var(--ember-hot)', filter: 'drop-shadow(0 0 20px rgba(246,233,200,0.9))' }}
          >
            ✧
          </motion.span>
        </motion.div>
        <SceneText
          delay={1.9}
          warm
          eyebrow={t('hud.planUnlimitedEyebrow', 'plano unlimited')}
          title={t('hud.planUnlimitedTitle', 'O céu inteiro é seu.')}
        />
      </div>
    </Scene>
  );
}

/* ───────────────────────── contratação: o contrato de dois lacres ───────────────────────── */
const HIRED_SIGN_LINES = [
  { width: '72%', delay: 0.85 },
  { width: '48%', delay: 1.05 },
];

function HiredMoment({ name, onDone }: { name: string; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4600);
  return (
    <Scene onDone={onDone}>
      <div className="flex flex-col items-center gap-7 px-6">
        <motion.div
          initial={{ y: 30, opacity: 0, rotate: -1.5 }}
          animate={{ y: 0, opacity: 1, rotate: -1 }}
          transition={{ type: 'spring', stiffness: 140, damping: 15 }}
          className="relative w-[min(300px,86vw)] rounded-[3px_8px_8px_3px] px-7 pb-10 pt-7 text-center"
          style={{
            background: 'linear-gradient(180deg,#F6F1E4,#EAE2CE)',
            boxShadow: '0 40px 90px -20px rgba(0,0,0,0.8), 0 0 0 1px rgba(70,55,30,0.25)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <p className="font-mono text-[7.5px] uppercase tracking-[0.28em] text-[#A8935F]">
            {t('hud.hiredContractLabel', 'contrato firmado')}
          </p>
          <p className="mt-2 font-serif text-[20px] font-medium leading-tight text-[#241C36]">{name}</p>
          {/* as duas assinaturas se desenham */}
          <div className="mx-auto mt-4 flex w-[70%] flex-col items-center gap-2">
            {HIRED_SIGN_LINES.map((ln, i) => (
              <motion.span
                key={i}
                aria-hidden
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: ln.width, opacity: 1 }}
                transition={{ delay: ln.delay, duration: 0.5, ease: [0.4, 0, 0.4, 1] }}
                className="block h-[2px] rounded-full"
                style={{ background: 'linear-gradient(90deg, transparent, #2A2438 20%, #2A2438)' }}
              />
            ))}
          </div>
          {/* os dois lacres estampam a base do contrato, um após o outro */}
          <div className="pointer-events-none absolute inset-x-0 -bottom-6 flex items-center justify-center gap-5" aria-hidden>
            <motion.span
              initial={{ scale: 2.2, opacity: 0, rotate: -30 }}
              animate={{ scale: 1, opacity: 1, rotate: -8 }}
              transition={{ delay: 1.6, type: 'spring', stiffness: 320, damping: 14 }}
              className="grid h-[48px] w-[48px] place-items-center rounded-full text-[16px]"
              style={{
                background: 'radial-gradient(circle at 36% 30%, var(--ember-hot), var(--ember) 55%, #9A8756)',
                color: '#141005',
                boxShadow: '0 8px 22px rgba(0,0,0,0.5), 0 0 24px rgba(223,201,147,0.45)',
              }}
            >
              ✦
            </motion.span>
            <motion.span
              initial={{ scale: 2.2, opacity: 0, rotate: 30 }}
              animate={{ scale: 1, opacity: 1, rotate: 8 }}
              transition={{ delay: 2.0, type: 'spring', stiffness: 320, damping: 14 }}
              className="grid h-[48px] w-[48px] place-items-center rounded-full text-[16px]"
              style={{
                background: 'radial-gradient(circle at 36% 30%, #5EEAD4, #2E9E8A 55%, #164039)',
                color: '#07211D',
                boxShadow: '0 8px 22px rgba(0,0,0,0.5), 0 0 24px rgba(94,234,212,0.4)',
              }}
            >
              ✧
            </motion.span>
          </div>
        </motion.div>
        <SceneText
          delay={2.5}
          warm
          eyebrow={t('hud.hiredEyebrow', 'aliança firmada')}
          title={t('hud.hiredTitle', 'A jornada tem um novo guardião.')}
          sub={name}
        />
      </div>
    </Scene>
  );
}

export { PlanFreeMoment, PlanStarterMoment, PlanProfessionalMoment, PlanUnlimitedMoment, HiredMoment };
